import { Maybe } from '@interfaces';
import { ILandingPageProps, IPageProps } from '../../interfaces/IPages';
import { ITab } from '../components/molecules/nav-tabs/nav-tabs';
import { __ } from '../utils/translation-service';

import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { ILabelIconProps } from '../components/atoms/label-icon/label-icon';
import { FloatingStickyBar } from '../components/molecules/floating-sticky-bar/floating-sticky-bar';
import { DeviceContext } from '../contexts/device/device';
import { theme, mediaQueries } from '../style/global-style';
import { normalizeLabels } from '../utils/normalizeLabels';
import LandingpageStageSearch from '../components/sections/landingpage-stage-search/landingpage-stage-search';
import { eventTracker } from 'src/utils/EventTracker';
import { numberFormat, replaceVal } from 'src/utils/formatting';
import { searchCruiseFacets, searchCruises } from '../api/resources';
import { ICruiseSearch, ICruiseSearchFacets } from '../../../interfaces/cruise';
import { ITabMenuItem, sectionNames } from '../../src/constants/shipLanding';
export interface IStageProps {
    companyImage?: string;
    backgroundImage: string;
    icons: ILabelIconProps[];
}
interface StageModInternalData {
    cruiseSearchlandingpageStage: Maybe<ICruiseSearch>;
    cruiseSearchFacetslandingpage: Maybe<ICruiseSearchFacets>;
}

export const StageMod = ({
    pageProps,
    ugcProps,
    navItems,
    currentTabIndex,
    onTabMenuSelect,
    onSearchSubmit,
    stageModuleType,
}: {
    pageProps: IPageProps;
    ugcProps: any;
    navItems: ITabMenuItem[];
    currentTabIndex: number;
    onTabMenuSelect?: (selectedMenuItem?: ITabMenuItem) => void;
    onSearchSubmit: () => void;
    stageModuleType: string | null;
}) => {
    const [stageData, setStageData] = React.useState({
        cruiseSearchlandingpageStage: null,
        cruiseSearchFacetslandingpage: null,
    } as StageModInternalData);

    React.useEffect(() => {
        Promise.all([
            // TODO: we are doing almost similar fetch in [...landingpage] component
            // but with limit: 6 maybe we can reuse data we from that request or idk
            // added it here because in graph ql query  property `cruiseSearchlandingpageStage` has separate request with limit=3
            searchCruises({
                slug: pageProps.getLandingPage.slug,
                limit: 3,
                sortBy: 'relevance',
            }),
            searchCruiseFacets({
                shipId: pageProps.getLandingPage.id,
                limit: 9999,
            }),
        ])
            .then((data) => {
                const [cruiseSearchlandingpageStage, cruiseSearchFacetslandingpage] = data;

                setStageData({
                    cruiseSearchlandingpageStage,
                    cruiseSearchFacetslandingpage,
                });
            })
            .catch((err) => {
                throw Error('Stage mod data fetch failed');
            });
    }, []);

    // Dummy variable
    const device = React.useContext(DeviceContext);
    const { getLandingPage, modulesData } = pageProps;
    const highestDiscount = modulesData?.cruiseSearchlandingpageHighestDiscount;
    const cheapestPrice = modulesData?.cruiseSearchlandingpageCheapestPrice;

    const observerRef = React.useRef<HTMLDivElement | null>(null);
    const cruisesearchFacetsData = stageData?.cruiseSearchFacetslandingpage ?? {
        kind: '',
        currentItemCount: 0,
        totalCount: 0,
        items: [],
    };

    let props: IStageProps = getShipProps(pageProps);

    const maxDiscount = highestDiscount?.items[0]?.sailings?.maxDiscount;
    const minSailPrice = cheapestPrice?.items[0]?.sailings?.minPrice;
    const minPrice = minSailPrice ? Number((minSailPrice / 2).toFixed(0)) : undefined; // p.P price
    const totalCount = stageData?.cruiseSearchlandingpageStage?.totalCount ?? 0;

    const icons: ILabelIconProps[] =
        props?.icons?.length > 0
            ? props.icons.map((icon) => ({
                  ...icon,
                  iconColor: theme.colors.main4,
                  fontColor: theme.shades.blackGrey,
                  horizontal: true,
              }))
            : [];

    const ugcLabels = stageData?.cruiseSearchlandingpageStage?.items?.[0]?.ugcLabels ?? [];

    const searchResult = stageData.cruiseSearchlandingpageStage || {
        items: [],
        totalCount: 0,
    };

    const labelItems =
        normalizeLabels({
            ugcLabels: ugcLabels || [],
            excludeCluster: ['constellation', 'productData'],
        }) || [];

    const constellationLabels =
        normalizeLabels({
            ugcLabels: ugcLabels || [],
            excludeCluster: [
                'productData',
                'shipRatings',
                'experienceLabels',
                'ageRange',
                'routeRatings',
                'atmosphere',
                'ugcReview',
            ],
        }) || [];

    const handleTabSelect = (tab: number) => {
        if (tab < 0 || tab > navItems.length) return;

        const selectedMenuItem = navItems.find((item) => item.name === navItems[tab].name);

        if (!selectedMenuItem) {
            return false;
        }

        eventTracker.track({
            event: 'click',
            category: 'sticky-navigation',
            action: `navigation-${selectedMenuItem.name}`,
            label: `${getLandingPage.type}-${getLandingPage.title}`,
        });

        onTabMenuSelect?.(selectedMenuItem);
    };

    const getNavItems = useMemo(() => {
        return navItems.reduce((acc, nav) => {
            let tabTitle = __(nav.titleTranslationKey);
            if (nav.name === sectionNames.reviews) {
                if (!ugcProps || ugcProps.totalRatings === 0) {
                    return acc;
                }
                tabTitle = replaceVal(
                    tabTitle,
                    numberFormat(ugcProps.totalRatings, { maxDecimals: 1 }),
                    /\[amount]/g,
                );
            }
            acc.push({
                key: nav.name,
                title: tabTitle,
            });
            return acc;
        }, [] as ITab[]);
    }, []);

    return (
        <>
            {stageModuleType === 'searchStage' && (
                <LandingpageStageSearch
                    totalSearchResultCount={searchResult?.totalCount || 0}
                    highestDiscount={maxDiscount}
                    type={getLandingPage.type}
                    backgroundImage={props?.backgroundImage}
                    title={getLandingPage.title}
                    companyImage={props?.companyImage ? props.companyImage : ''}
                    onSearchSubmit={onSearchSubmit}
                    cruiseSearchFacets={cruisesearchFacetsData}
                    pageTypeId={getLandingPage.id}
                    pageProps={pageProps}
                />
            )}

            {device !== 'mobile' && (
                <StyledOffsetMargin offsetDown={stageModuleType === 'searchStage'} />
            )}

            <div ref={observerRef}>
                <FloatingStickyBar
                    title={getLandingPage.title}
                    icons={icons}
                    labelItems={labelItems.map((label) => ({
                        type: label.label,
                        label: label.label,
                    }))}
                    constellationLabels={constellationLabels.map((label) => ({
                        type: label.label,
                        label: label.label,
                    }))}
                    ratingValue={ugcProps?.ratingValue}
                    ratingString={ugcProps?.ratingString}
                    ratingAmount={ugcProps?.totalRatings}
                    price={minPrice}
                    discount={maxDiscount}
                    totalCount={totalCount}
                    navItems={getNavItems}
                    stageModuleType={stageModuleType}
                    onTabSelect={handleTabSelect}
                    currentTabIndex={currentTabIndex}
                    onSearchSubmit={onSearchSubmit}
                    observerRef={observerRef}
                />
            </div>
        </>
    );
};

const getShipProps = ({
    getLandingPage,
    modulesData,
}: ILandingPageProps['pageProps']): IStageProps => {
    const shipModuleData = modulesData[`ship${getLandingPage.id}`];
    if (!shipModuleData) {
        throw new Error('getShipProps called without data');
    }
    // @TODO: fix thumbor types
    const companyImage = shipModuleData?.company?.logoFilepath || '';
    const backgroundImage = shipModuleData?.bannerImage || '';

    const icons: ILabelIconProps[] = [];
    const data = modulesData[`${getLandingPage.type}${getLandingPage.id}`];

    if (data?.factsAndFigures) {
        const { manufactureYear, passengerCapacity, crewMembers, decks } = data.factsAndFigures;

        if (manufactureYear) {
            icons.push({ type: 'buildYear', amount: `${manufactureYear}` });
        }
        if (passengerCapacity) {
            icons.push({ type: 'passenger', amount: `${passengerCapacity}` });
        }
        if (crewMembers) {
            icons.push({ type: 'crew', amount: `${crewMembers}` });
        }
        if (decks) {
            icons.push({ type: 'decks', amount: `${decks}` });
        }
    }

    return {
        companyImage,
        backgroundImage,
        icons,
    };
};

const StyledOffsetMargin = styled.div`
    margin-bottom: ${(props: { offsetDown: boolean }) => (props.offsetDown ? '40px' : '-40px')};

    @media ${mediaQueries.allTablet} {
        margin-bottom: ${(props: { offsetDown: boolean }) => (props.offsetDown ? '40px' : '-30px')};
    }
`;
